// Live Events — auto-advance through an event's ranked channels when one fails.
//
// The field report this exists for: "the 3-4 events I tried to play via Watch
// best either gave me channels that were dead / not playing and I was stuck,
// having to go back manually to the events page and search the event again."
//
// Two things were wrong. The ranked candidate list — the whole point of the
// Event Finder — was thrown away the moment we navigated to /live, so recovery
// meant starting the search over. And recovery only triggered on a FATAL player
// error, while the reported failure was "dead / not playing": a stream that
// never comes up produces no fatal error at all, so nothing fired and the user
// sat there.
//
// So this arms on arrival from an event, keeps the list, and treats BOTH
// signals as failure:
//   - a fatal player error, and
//   - no first frame within CONNECT_TIMEOUT_MS (the silent case).
//
// It disarms the moment the user takes manual control, because auto-advancing
// away from a channel someone deliberately chose is worse than any dead stream.

import { computed, ref } from 'vue';
import { api, type EventCandidate } from '@/lib/api';
import { log } from '@/lib/log';
import { isTuneInConnected, subscribeStreamConnected } from '@/composables/useObservationCapture';

/**
 * How long a tune-in may show nothing before we call it dead.
 *
 * Generous on purpose: a slow upstream on a cold provider connection can take
 * several seconds to first frame, and advancing off a channel that was about to
 * work is the one failure mode worse than waiting. 12s is well past the point
 * where a working stream has produced `loadedmetadata`.
 */
export const CONNECT_TIMEOUT_MS = 12_000;

/**
 * Total tune-ins per event, including the first. Bounded because a provider
 * having a bad night can fail every channel, and silently cycling 40 of them
 * burns the account's connection budget while telling the user nothing.
 */
export const MAX_ATTEMPTS = 5;

export type AutoAdvanceTarget = { sourceId: number; itemId: string; name: string };

type State = {
    eventKey: string;
    reference: { kind: 'discovery' | 'fixture'; key: string; date?: string; timezone?: string; countries?: string[] };
    eventTitle: string;
    candidates: AutoAdvanceTarget[];
    index: number;
    attempts: number;
};

let state: State | null = null;
let armGeneration = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let unsubscribe: (() => void) | null = null;

const armed = ref(false);
const advancing = ref(false);
const lastOutcome = ref<'connected' | 'exhausted' | 'disarmed' | null>(null);

type Handlers = {
    /** Tune a channel. Resolves when the tune-in has been dispatched. */
    tune: (target: AutoAdvanceTarget) => Promise<void>;
    /** Tell the user what just happened. */
    notify: (message: string, kind: 'info' | 'error', action?: { label: string; run: () => void }) => void;
    /** Send the user back to the event's full channel list. */
    openEvent: (eventKey: string) => void;
    openFixture?: (fixtureKey: string, context?: { date?: string; timezone?: string; countries?: string[] }) => void;
};

let handlers: Handlers | null = null;

export function configureEventAutoAdvance(next: Handlers): void {
    handlers = next;
}

function clearTimer(): void {
    if (timer) clearTimeout(timer);
    timer = null;
}

function stopListening(): void {
    unsubscribe?.();
    unsubscribe = null;
}

/** The candidate currently being tried, if any. */
function currentTarget(): AutoAdvanceTarget | null {
    if (!state) return null;
    return state.candidates[state.index] ?? null;
}

/**
 * Arm for one event. `startedWith` is the channel the user clicked, so the
 * sequence continues AFTER it rather than retrying it first.
 */
export async function armEventAutoAdvance(
    eventKey: string,
    startedWith: { sourceId: number; itemId: string },
): Promise<void> {
    await armAutoAdvance({ kind: 'discovery', key: eventKey }, startedWith);
}

export async function armFixtureAutoAdvance(
    fixtureKey: string,
    startedWith: { sourceId: number; itemId: string },
    context: { date?: string; timezone?: string; countries?: string[] } = {},
): Promise<void> {
    await armAutoAdvance({ kind: 'fixture', key: fixtureKey, ...context }, startedWith);
}

async function armAutoAdvance(
    reference: { kind: 'discovery' | 'fixture'; key: string; date?: string; timezone?: string; countries?: string[] },
    startedWith: { sourceId: number; itemId: string },
): Promise<void> {
    disarm('rearm');
    const generation = armGeneration;
    try {
        let candidates: AutoAdvanceTarget[];
        let eventTitle: string;
        if (reference.kind === 'fixture') {
            const detail = await api.events.fixture(reference.key, {
                date: reference.date,
                timezone: reference.timezone,
                countries: reference.countries,
            });
            if (!('fixture' in detail)) return;
            candidates = detail.candidates.guideConfirms
                .concat(detail.candidates.listings, detail.candidates.possible)
                .filter((candidate) => candidate.autoEligible)
                .map((candidate) => ({ sourceId: candidate.sourceId, itemId: candidate.itemId, name: candidate.name }));
            eventTitle = `${detail.fixture.home.name} vs ${detail.fixture.away.name}`;
        } else {
            const detail = await api.events.discoveryDetail(reference.key, { limit: MAX_ATTEMPTS * 4 });
            candidates = (detail.candidates as EventCandidate[])
                .map((candidate) => ({ sourceId: candidate.sourceId, itemId: candidate.itemId, name: candidate.name }));
            eventTitle = detail.event.title;
        }
        if (generation !== armGeneration) return;
        const seen = new Set<string>();
        candidates = candidates.filter((candidate) => {
            const identity = `${candidate.sourceId}:${candidate.itemId}`;
            if (seen.has(identity)) return false;
            seen.add(identity);
            return true;
        });
        if (candidates.length === 0) return;
        const startIndex = candidates.findIndex(
            (c) => c.sourceId === startedWith.sourceId && c.itemId === startedWith.itemId,
        );
        state = {
            eventKey: reference.key,
            reference,
            eventTitle,
            candidates,
            // An unlisted starting channel means the user came from somewhere
            // else; begin at the top of the ranking rather than guessing.
            index: startIndex >= 0 ? startIndex : 0,
            attempts: 1,
        };
        armed.value = true;
        lastOutcome.value = null;
        listen();
        watchdog();
        // A very fast first frame can land while the fixture detail request is
        // resolving. Observation capture keeps the current tune result, so
        // replay its connected state after subscribing to close that race.
        if (isTuneInConnected(startedWith.sourceId, startedWith.itemId)) {
            noteConnected(startedWith.sourceId, startedWith.itemId);
        }
        log.info('eventAutoAdvance.armed', { candidates: candidates.length, index: state.index });
    } catch (err) {
        if (generation !== armGeneration) return;
        // Losing the list is not worth surfacing: the user still has a playing
        // (or failing) channel and every manual path is unchanged.
        log.warn('eventAutoAdvance.arm_failed', { error: (err as Error).message });
    }
}

function listen(): void {
    stopListening();
    unsubscribe = subscribeStreamConnected(noteConnected);
}

function noteConnected(sourceId: number, itemId: string): void {
    const target = currentTarget();
    // A connect for some OTHER channel means the user tuned away; that is
    // manual control, and manual control always wins.
    if (!target || target.sourceId !== sourceId || target.itemId !== itemId) {
        disarm('manual');
        return;
    }
    clearTimer();
    advancing.value = false;
    lastOutcome.value = 'connected';
    // Stay armed and reset the budget: a channel that works now and dies
    // twenty minutes into the match should still advance.
    if (state) state.attempts = 1;
    log.info('eventAutoAdvance.connected', {});
}

function watchdog(): void {
    clearTimer();
    timer = setTimeout(() => {
        // Nothing rendered and nothing errored — the silent-death case that
        // left the user staring at a dead player with no toast.
        log.info('eventAutoAdvance.connect_timeout', {});
        void advance('never started');
    }, CONNECT_TIMEOUT_MS);
    timer?.unref?.();
}

/** A fatal player error for the channel we are currently trying. */
export function reportEventPlaybackFailure(): boolean {
    if (!armed.value || !state) return false;
    void advance('stopped working');
    return true;
}

async function advance(reason: string): Promise<void> {
    if (!state || !handlers || advancing.value) return;
    clearTimer();
    const failed = currentTarget();
    const next = state.candidates[state.index + 1];

    if (!next || state.attempts >= MAX_ATTEMPTS) {
        const tried = state.attempts;
        const title = state.eventTitle;
        const eventKey = state.eventKey;
        const reference = state.reference;
        disarm('exhausted');
        lastOutcome.value = 'exhausted';
        handlers.notify(
            `Tried ${tried} channel${tried === 1 ? '' : 's'} for ${title} — none of them worked.`,
            'error',
            { label: 'Choose channel', run: () => reference.kind === 'fixture'
                ? handlers?.openFixture?.(reference.key, reference)
                : handlers?.openEvent(reference.key) },
        );
        log.warn('eventAutoAdvance.exhausted', { tried });
        return;
    }

    state.index += 1;
    state.attempts += 1;
    advancing.value = true;
    const position = state.attempts;
    handlers.notify(
        `${failed?.name ?? 'That channel'} ${reason} — trying ${next.name} (${position} of ${Math.min(MAX_ATTEMPTS, state.candidates.length)}).`,
        'info',
    );
    log.info('eventAutoAdvance.advancing', { attempt: position });
    const generation = armGeneration;
    try {
        await handlers.tune(next);
        if (generation !== armGeneration || !state || !armed.value) return;
        watchdog();
        // A quick backup may connect while tune() is still waiting for its
        // URL request to settle. The listener can clear the old timer; replay
        // the captured observation after arming the new watchdog as well.
        if (isTuneInConnected(next.sourceId, next.itemId)) {
            noteConnected(next.sourceId, next.itemId);
        }
        // Cleared as soon as the new tune is dispatched and its watchdog armed.
        // This flag exists to stop TWO advances racing (a fatal error landing in
        // the same tick as the timeout), not to stop the next one — leaving it
        // set meant the sequence could only ever step once, and then sat on a
        // dead channel forever, which is the exact bug being fixed here.
        advancing.value = false;
    } catch (err) {
        if (generation !== armGeneration || !state || !armed.value) return;
        advancing.value = false;
        log.warn('eventAutoAdvance.tune_failed', { error: (err as Error).message });
        void advance('could not be opened');
    }
}

/**
 * Stop advancing. Called on manual channel selection, on leaving the player,
 * and when the sequence ends — anything that means the user, not the machine,
 * is deciding what plays.
 */
export function disarm(reason: 'manual' | 'exhausted' | 'left' | 'rearm' | 'playback_stopped' | 'provider_limit' | 'media_recovery'): void {
    armGeneration += 1;
    clearTimer();
    stopListening();
    state = null;
    armed.value = false;
    advancing.value = false;
    if (reason === 'manual' || reason === 'left' || reason === 'playback_stopped') lastOutcome.value = 'disarmed';
}

export function useEventAutoAdvance() {
    return {
        isArmed: computed(() => armed.value),
        isAdvancing: computed(() => advancing.value),
        lastOutcome: computed(() => lastOutcome.value),
        arm: armEventAutoAdvance,
        armFixture: armFixtureAutoAdvance,
        disarm,
        reportFailure: reportEventPlaybackFailure,
        configure: configureEventAutoAdvance,
    };
}

// Test-only — drains module state between tests (Phase 2.6 retro #5).
export function _resetEventAutoAdvanceForTests(): void {
    armGeneration += 1;
    clearTimer();
    stopListening();
    state = null;
    handlers = null;
    armed.value = false;
    advancing.value = false;
    lastOutcome.value = null;
}

// Test-only — inspect the sequence without exporting mutable state.
export function _peekEventAutoAdvanceForTests(): {
    eventKey: string; index: number; attempts: number; candidates: number;
} | null {
    return state
        ? {
            eventKey: state.eventKey,
            index: state.index,
            attempts: state.attempts,
            candidates: state.candidates.length,
        }
        : null;
}
